import { Bookmark, Search, X } from "lucide-react";
import { useMemo, useState } from "react";
import { useLocation } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { useMarketTopology } from "@/hooks/useMarketIntelligence";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import {
  setMyViewsPanelOpen,
  useActiveMarketView,
  useMyViewsPanelOpen,
} from "@/hooks/useMyViewsPanel";
import { useMyMarketViews } from "@/hooks/useMarketViews";
import { groupKeyOf, groupMyViews } from "@/lib/market-intelligence/my-views";
import { MARKET_HUBS } from "@/lib/market-intelligence/topology";

import { MyViewsList } from "./MyViewsList";

/**
 * "My views": every chart view the reader has saved, from anywhere in Market
 * Intelligence, one click from opening — instead of finding the point, then
 * the tab, then the view menu.
 *
 * A panel opened by the top bar's bookmark (`MyViewsHeaderButton`): a column
 * at the right of the page, under the top bar and record bar, that PUSHES only
 * the page left rather than covering it — so a chart stays readable beside the
 * list, and the bars above stay put. The same place Energy Advisor's Maxwell
 * rail opens. Its header wears the top bar's grey; the list sits on white.
 * Closed, it takes no width at all. Desktop only (`lg`), and the bookmark is
 * not offered below it.
 *
 * Views are grouped by point (or region), the point on screen first, and the
 * view the chart is showing is marked. Search matches view, chart, point and
 * region names.
 */
export function MyViewsPanel() {
  const open = useMyViewsPanelOpen();
  const desktop = useMediaQuery("(min-width: 1024px)");
  if (!open || !desktop) return null;
  return <MyViewsPanelBody />;
}

/** The open panel — its own component so a closed panel fetches nothing. */
function MyViewsPanelBody() {
  const { pathname } = useLocation();
  const active = useActiveMarketView();
  const { data, isLoading, isError } = useMyMarketViews(true);
  const { data: topology } = useMarketTopology();
  const [query, setQuery] = useState("");

  const views = useMemo(() => data ?? [], [data]);
  const groups = useMemo(
    () =>
      groupMyViews(
        views,
        topology?.hubs ?? MARKET_HUBS,
        [...(topology?.isos ?? []), ...(topology?.gasRegions ?? [])],
        groupKeyOf(pathname),
        query,
      ),
    [views, topology, pathname, query],
  );

  return (
    <aside
      aria-label="My views"
      className="sticky top-chrome flex h-[calc(100vh-3.5rem-var(--viewing-as-bar-height))] w-[300px] shrink-0 flex-col self-start border-l border-border bg-card animate-in fade-in-0 slide-in-from-right-4"
    >
      <header className="flex shrink-0 items-center gap-2 border-b border-border bg-background-deep px-4 py-3">
        <span className="flex h-7 w-7 items-center justify-center rounded-md bg-brand-accent-dim text-brand-accent">
          <Bookmark size={14} />
        </span>
        <div className="flex min-w-0 flex-1 flex-col leading-tight">
          <span className="text-sm font-semibold text-foreground">My views</span>
          {isLoading ? (
            <Skeleton className="mt-1 h-3 w-20" />
          ) : (
            <span className="truncate text-xs text-muted-foreground">
              {views.length === 1 ? "1 saved view" : `${views.length} saved views`}
            </span>
          )}
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="size-7"
          aria-label="Close My views"
          onClick={() => setMyViewsPanelOpen(false)}
        >
          <X size={14} />
        </Button>
      </header>

      {views.length > 0 && (
        <div className="relative shrink-0 border-b border-border px-3 py-2">
          <Search
            size={13}
            className="pointer-events-none absolute left-5 top-1/2 -translate-y-1/2 text-text-dim"
            aria-hidden
          />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search views or points"
            aria-label="Search your views"
            className="h-8 pl-8"
          />
        </div>
      )}

      {/* Room at the foot so the last view scrolls clear of the floating
          Maxwell button, which sits over this corner. */}
      <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-20 pt-3">
        {isLoading ? (
          <div className="flex flex-col gap-2 px-2">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : isError ? (
          <p className="px-2 py-6 text-center text-sm text-muted-foreground">
            Couldn&apos;t load your views. Close and reopen to try again.
          </p>
        ) : views.length === 0 ? (
          <EmptyState
            icon={Bookmark}
            title="No saved views yet"
            description="Set up any chart the way you like it, then choose Save as new view from the ⋮ menu beside View. Every view you save shows up here, from anywhere in Market Intelligence."
            className="px-4"
          />
        ) : groups.length === 0 ? (
          <p className="px-2 py-6 text-center text-sm text-muted-foreground">
            No views match &ldquo;{query.trim()}&rdquo;.
          </p>
        ) : (
          <MyViewsList groups={groups} active={active} />
        )}
      </div>
    </aside>
  );
}
